import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Check, Plus } from 'lucide-react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { toast } from 'sonner'
import { StorefrontSettingsGuard } from './StorefrontSettingsGuard'
import { SettingRow, Divider } from './SettingRow'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import { usePageTranslation } from '@/i18n/useTranslation'
import { contentTranslations } from '@/lib/erp/contentTranslations'

// Popular languages shown as rows by default. The FULL list comes dynamically
// from the Frappe Language DocType (~200 languages) via the "Add language"
// picker — nothing is limited to this set. nativeName is display sugar only.
const POPULAR_LANGUAGES = [
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
]

const translations = {
  en: {
    title: 'Languages',
    subtitle: 'Let customers browse your store in more than one language',
    storeLanguages: 'Store languages',
    storeLanguagesHint:
      'Enable the languages your products are translated into. Customers can switch languages on your storefront.',
    defaultLabel: 'Default',
    makeDefault: 'Make default',
    defaultHint:
      'The default language is what your product data is written in. Other languages show translations you add on each product.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Language settings saved',
    saveFailed: 'Could not save language settings',
    back: 'Storefront Settings',
    addLanguage: 'Add another language',
    searchLanguage: 'Search languages…',
    noLanguageFound: 'No language found',
  },
  th: {
    title: 'ภาษา',
    subtitle: 'ให้ลูกค้าเลือกดูร้านค้าได้มากกว่าหนึ่งภาษา',
    storeLanguages: 'ภาษาของร้านค้า',
    storeLanguagesHint:
      'เปิดใช้ภาษาที่คุณแปลสินค้าไว้ ลูกค้าจะสลับภาษาได้จากหน้าร้าน',
    defaultLabel: 'ค่าเริ่มต้น',
    makeDefault: 'ตั้งเป็นค่าเริ่มต้น',
    defaultHint:
      'ภาษาเริ่มต้นคือภาษาของข้อมูลสินค้าหลัก ภาษาอื่นจะแสดงคำแปลที่คุณเพิ่มในแต่ละสินค้า',
    save: 'บันทึก',
    saving: 'กำลังบันทึก…',
    saved: 'บันทึกการตั้งค่าภาษาแล้ว',
    saveFailed: 'บันทึกการตั้งค่าภาษาไม่สำเร็จ',
    back: 'ตั้งค่าหน้าร้าน',
    addLanguage: 'เพิ่มภาษาอื่น',
    searchLanguage: 'ค้นหาภาษา…',
    noLanguageFound: 'ไม่พบภาษา',
  },
}

function parseEnabled(raw: string | undefined | null): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string') : []
  } catch {
    return []
  }
}

export function LanguagesPage() {
  const navigate = useNavigate()
  const t = usePageTranslation(translations)
  const { settings, isLoading, updateAsync, isUpdating } = useStorefrontTenantSettings()

  const [enabled, setEnabled] = useState<string[]>([])
  const [defaultLang, setDefaultLang] = useState<string>('')
  const [pickerOpen, setPickerOpen] = useState(false)

  // Full language catalog from the Frappe Language DocType (~200, shared core data)
  const { data: allLanguages } = useQuery({
    queryKey: ['erp-languages'],
    queryFn: () => contentTranslations.listLanguages(),
    staleTime: 24 * 60 * 60 * 1000, // reference data — changes ~never
  })

  const languageName = useCallback(
    (code: string) => {
      const popular = POPULAR_LANGUAGES.find((l) => l.code === code)
      if (popular) return `${popular.name} (${popular.nativeName})`
      const known = allLanguages?.find((l) => l.name === code)
      return known ? known.language_name : code
    },
    [allLanguages],
  )

  // Rows = popular set ∪ anything already enabled (e.g. added via picker)
  const visibleCodes = useMemo(() => {
    const codes = POPULAR_LANGUAGES.map((l) => l.code)
    for (const code of enabled) if (!codes.includes(code)) codes.push(code)
    return codes
  }, [enabled])

  // English + Thai display names so search matches "Korean"/"เกาหลี", not only "한국어"
  const displayName = useCallback((code: string, locale: string): string => {
    try {
      return new Intl.DisplayNames([locale], { type: 'language' }).of(code.replace(/_/g, '-')) ?? ''
    } catch {
      return ''
    }
  }, [])

  // Picker options = everything else, enriched with searchable EN/TH names
  const pickerOptions = useMemo(
    () =>
      (allLanguages ?? [])
        .filter((l) => !visibleCodes.includes(l.name))
        .map((l) => {
          const en = displayName(l.name, 'en')
          const th = displayName(l.name, 'th')
          return {
            code: l.name,
            nativeName: l.language_name,
            englishName: en && en !== l.name ? en : '',
            searchValue: [l.language_name, l.name, en, th].filter(Boolean).join(' '),
          }
        }),
    [allLanguages, visibleCodes, displayName],
  )

  useEffect(() => {
    if (!settings) return
    const fromServer = parseEnabled(settings.enabled_languages)
    const serverDefault = settings.storefront_language || 'th'
    // Default language is always part of the enabled set in the UI
    setEnabled(fromServer.includes(serverDefault) ? fromServer : [serverDefault, ...fromServer])
    setDefaultLang(serverDefault)
  }, [settings])

  const toggleLanguage = useCallback(
    (code: string, on: boolean) => {
      setEnabled((prev) => {
        if (on) return prev.includes(code) ? prev : [...prev, code]
        if (code === defaultLang) return prev // default cannot be disabled
        return prev.filter((c) => c !== code)
      })
    },
    [defaultLang],
  )

  const handleSave = useCallback(async () => {
    try {
      await updateAsync({
        enabled_languages: JSON.stringify(enabled),
        storefront_language: defaultLang,
      })
      toast.success(t.saved)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.saveFailed)
    }
  }, [updateAsync, enabled, defaultLang, t])

  const serverEnabled = parseEnabled(settings?.enabled_languages)
  const hasChanges =
    !!settings &&
    (JSON.stringify(enabled) !== JSON.stringify(serverEnabled) ||
      defaultLang !== (settings.storefront_language || 'th'))

  return (
    <StorefrontSettingsGuard>
      <PageContainer>
        <div className="p-6 space-y-6 max-w-2xl">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 -ml-2 text-muted-foreground"
              onClick={() => navigate('/storefront/settings')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t.back}
            </Button>
            <h2 className="text-2xl font-semibold">{t.title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
          </div>

          <Card className="rounded-2xl border-0 shadow-apple-sm">
            <CardContent className="p-2">
              <div className="px-4 pt-4 pb-1">
                <h3 className="text-sm font-semibold">{t.storeLanguages}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{t.storeLanguagesHint}</p>
              </div>
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                visibleCodes.map((code, idx) => {
                  const isOn = enabled.includes(code)
                  const isDefault = defaultLang === code
                  return (
                    <div key={code}>
                      {idx > 0 && <Divider />}
                      <SettingRow
                        label={languageName(code)}
                        subtitle={code}
                      >
                        <div className="flex items-center gap-3">
                          {isDefault ? (
                            <Badge variant="secondary">{t.defaultLabel}</Badge>
                          ) : (
                            isOn && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-muted-foreground"
                                onClick={() => setDefaultLang(code)}
                              >
                                {t.makeDefault}
                              </Button>
                            )
                          )}
                          <Switch
                            checked={isOn}
                            disabled={isDefault}
                            onCheckedChange={(on) => toggleLanguage(code, on)}
                          />
                        </div>
                      </SettingRow>
                    </div>
                  )
                })
              )}
            </CardContent>
          </Card>

          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-sm">
                <Plus className="h-4 w-4 mr-1.5" />
                {t.addLanguage}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-72" align="start">
              <Command>
                <CommandInput placeholder={t.searchLanguage} />
                <CommandList>
                  <CommandEmpty>{t.noLanguageFound}</CommandEmpty>
                  <CommandGroup>
                    {pickerOptions.map((lang) => (
                      <CommandItem
                        key={lang.code}
                        value={lang.searchValue}
                        onSelect={() => {
                          toggleLanguage(lang.code, true)
                          setPickerOpen(false)
                        }}
                      >
                        <span className="flex-1">
                          {lang.nativeName}
                          {lang.englishName && lang.englishName !== lang.nativeName && (
                            <span className="text-muted-foreground"> — {lang.englishName}</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{lang.code}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <p className="text-xs text-muted-foreground px-1">{t.defaultHint}</p>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isUpdating || !hasChanges || isLoading}>
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t.save}
                </>
              )}
            </Button>
          </div>
        </div>
      </PageContainer>
    </StorefrontSettingsGuard>
  )
}
